import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, PlusSquare, Share, X } from 'lucide-react';

/**
 * Proposer d'installer FCONNECT sur l'écran d'accueil.
 *
 * Android (Chrome, Edge, Samsung Internet) fournit un vrai bouton
 * d'installation via `beforeinstallprompt`. Safari sur iPhone n'en a pas :
 * l'installation passe par « Partager → Sur l'écran d'accueil », qu'on
 * explique donc pas à pas. Rien ne s'affiche une fois l'app installée, dans une
 * page intégrée chez un tiers (billetterie), ni pendant 14 jours après « Plus
 * tard ».
 */

interface EvenementInstallation extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const CLE_REPORT = 'fc-installation-reportee';
const DELAI_REPORT_MS = 14 * 24 * 60 * 60 * 1000;

function dejaInstallee(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function estIos(): boolean {
  const ua = navigator.userAgent;
  // Les iPad récents se présentent comme un Mac : on les reconnaît au tactile.
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

function reporteRecemment(): boolean {
  try {
    const valeur = Number(localStorage.getItem(CLE_REPORT));
    return Number.isFinite(valeur) && Date.now() - valeur < DELAI_REPORT_MS;
  } catch {
    return false;
  }
}

function reporter() {
  try {
    localStorage.setItem(CLE_REPORT, String(Date.now()));
  } catch {
    /* navigation privée : la bannière reviendra, sans gravité */
  }
}

export default function InstallerApp() {
  const { pathname } = useLocation();
  const [invite, setInvite] = useState<EvenementInstallation | null>(null);
  const [ios] = useState(estIos);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (dejaInstallee() || window.self !== window.top) return;

    const surInvite = (evenement: Event) => {
      // On garde l'invitation pour la proposer dans notre bannière.
      evenement.preventDefault();
      setInvite(evenement as EvenementInstallation);
    };
    const surInstallee = () => {
      setInvite(null);
      setVisible(false);
    };
    window.addEventListener('beforeinstallprompt', surInvite);
    window.addEventListener('appinstalled', surInstallee);

    // Laisser la page se charger avant de proposer quoi que ce soit.
    const minuteur = window.setTimeout(() => setVisible(!reporteRecemment()), 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', surInvite);
      window.removeEventListener('appinstalled', surInstallee);
      window.clearTimeout(minuteur);
    };
  }, []);

  if (!visible || pathname.startsWith('/billetterie')) return null;
  if (!invite && !ios) return null;

  const fermer = () => {
    reporter();
    setVisible(false);
  };

  const installer = async () => {
    if (!invite) return;
    await invite.prompt();
    const { outcome } = await invite.userChoice;
    setInvite(null);
    if (outcome === 'dismissed') reporter();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Installer FCONNECT"
      className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl sm:inset-x-auto sm:right-4"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-start gap-3">
        <img src="/app-icons/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900">Installer FCONNECT</p>
          {invite ? (
            <p className="mt-0.5 text-sm text-gray-600">
              Ajoute l'application à ton écran d'accueil : elle s'ouvre en plein écran, comme une app.
            </p>
          ) : (
            <ol className="mt-1 space-y-1 text-sm text-gray-600">
              <li>
                1. Touche{' '}
                <Share className="inline h-4 w-4 -translate-y-0.5 text-blue-600" aria-label="Partager" /> dans
                Safari
              </li>
              <li>
                2. Choisis <PlusSquare className="inline h-4 w-4 -translate-y-0.5" aria-hidden /> « Sur l'écran
                d'accueil »
              </li>
            </ol>
          )}
        </div>
        <button
          type="button"
          onClick={fermer}
          className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Plus tard"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      {invite && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={fermer}
            className="min-h-[44px] rounded-lg px-4 text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={installer}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-green-600 px-4 text-sm font-semibold text-white hover:bg-green-700"
          >
            <Download className="h-4 w-4" /> Installer
          </button>
        </div>
      )}
    </div>
  );
}
